/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BASE_URL } from '../injection-tokens';
import { Rule } from '../models/rule/rule.interface';
import { PaginatedHttpClient } from '../paginated-http-client';

const HTTP_STATUS_OK = 200;

@Injectable({
  providedIn: 'root',
})
export class RuleApiService extends PaginatedHttpClient<Rule> {
  private readonly baseUrl: string;

  constructor() {
    const http = inject(HttpClient);
    const baseUrl = inject(BASE_URL);

    super(http, baseUrl + '/rules');
    this.baseUrl = baseUrl;
  }

  override getAllByParams(params: HttpParams, headers?: HttpHeaders) {
    return super.getAllByParams(params, headers);
  }

  override getOne(id: string, headers?: HttpHeaders): Observable<Rule> {
    return super.getOne(id, headers);
  }

  createRule(rule: Rule, headers?: HttpHeaders): Observable<boolean> {
    return super
      .getHttp()
      .post<any>(super.getApiUrl(), rule, { observe: 'response', headers })
      .pipe(map((response: HttpResponse<void>) => response.status === HTTP_STATUS_OK));
  }

  patchRule(partialRule: { id: string; [key: string]: any }, headers?: HttpHeaders): Observable<boolean> {
    return super
      .getHttp()
      .patch<any>(super.getApiUrl() + '/' + partialRule.id, partialRule, { observe: 'response', headers })
      .pipe(map((response: HttpResponse<void>) => response.status === HTTP_STATUS_OK));
  }

  deleteRule(id: string, headers?: HttpHeaders): Observable<boolean> {
    return super
      .getHttp()
      .delete<any>(super.getApiUrl() + '/' + id, { observe: 'response', headers })
      .pipe(map((response: HttpResponse<void>) => response.status === HTTP_STATUS_OK));
  }

  check(rule: Rule, headers?: HttpHeaders): Observable<boolean> {
    return super
      .getHttp()
      .post<any>(super.getApiUrl() + '/check', rule, { observe: 'response', headers })
      .pipe(map((response: HttpResponse<void>) => response.status === HTTP_STATUS_OK));
  }

  export(headers?: HttpHeaders): Observable<any> {
    return super.getHttp().get(super.getApiUrl() + '/export', { headers, responseType: 'text' });
  }

  prepareSignedExport(headers?: HttpHeaders): Observable<string> {
    return super
      .getHttp()
      .post(super.getApiUrl() + '/export/signed-url', null, { headers, responseType: 'text' })
      .pipe(map((url) => this.baseUrl + url));
  }
}
